import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Axios Instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request Interceptor - Token hinzufügen
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor - Logout bei 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth Services
export const authService = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },

  verify: async () => {
    const response = await api.get('/auth/verify');
    return response.data;
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  }
};

// User Services
export const userService = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  create: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  update: async (id, userData) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  updateProfile: async (userData) => {
    const response = await api.put('/users/profile/me', userData);
    return response.data;
  }
};

// Booking Services
export const bookingService = {
  getAll: async (params) => {
    const response = await api.get('/bookings', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/bookings/${id}`);
    return response.data;
  },

  create: async (bookingData) => {
    const response = await api.post('/bookings', bookingData);
    return response.data;
  },

  update: async (id, bookingData) => {
    const response = await api.put(`/bookings/${id}`, bookingData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/bookings/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/bookings/stats/overview');
    return response.data;
  }
};

// Customer Services
export const customerService = {
  getAll: async (params) => {
    const response = await api.get('/customers', { params });
    return response.data;
  },

  getByEmail: async (email) => {
    const response = await api.get(`/customers/${encodeURIComponent(email)}`);
    return response.data;
  }
};

// Inquiry Services
export const inquiryService = {
  getAll: async (params) => {
    const response = await api.get('/inquiries', { params });
    return response.data;
  },

  getById: async (type, id) => {
    const response = await api.get(`/inquiries/${type}/${id}`);
    return response.data;
  },

  convertToBooking: async (id) => {
    const response = await api.post(`/inquiries/${id}/convert-to-booking`);
    return response.data;
  },

  convertToFeierpaket: async (id) => {
    const response = await api.post(`/inquiries/${id}/convert-to-feierpaket`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/inquiries/stats/overview');
    return response.data;
  }
};

// Anfragen-Pipeline (neue Website-Anfragen)
export const anfrageService = {
  getAll: async () => (await api.get('/anfragen')).data,
  getById: async (id) => (await api.get(`/anfragen/${id}`)).data,
  create: async (daten) => (await api.post('/anfragen', daten)).data,
  update: async (id, daten) => (await api.patch(`/anfragen/${id}`, daten)).data,
  pipeline: async () => (await api.get('/anfragen/stats/pipeline')).data,
  upload: async (id, art, datei, statusSetzen = true) => {
    const base64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(',')[1]);
      r.onerror = reject;
      r.readAsDataURL(datei);
    });
    return (await api.post(`/anfragen/${id}/datei/${art}`, { filename: datei.name, contentType: datei.type || 'application/pdf', base64, statusSetzen })).data;
  },
  vertrag: async (id, daten, speichern = false) => {
    const res = await api.post(`/anfragen/${id}/vertrag${speichern ? '?speichern=1' : ''}`, daten, { responseType: 'blob' });
    return res.data; // PDF als Blob
  }
};

export default api;
